import { Alert, Collapse, Form, InputNumber } from '@arco-design/web-react'
import InstantSwitch from './InstantSwitch'
import SettingSectionCard from './SettingSectionCard'
import JsonField, { validateJson } from './JsonField'
import RateLimitGroupEditor, {
  parseRateLimitGroup,
  validateRateLimitGroupRows,
} from './rateLimit/RateLimitGroupEditor'
import type { OptionsAPI } from './useOptions'
import { Pill } from '@/utils/render'

const KEYS = [
  'ModelRequestRateLimitEnabled',
  'ModelRequestRateLimitDurationMinutes',
  'ModelRequestRateLimitCount',
  'ModelRequestRateLimitSuccessCount',
  'ModelRequestRateLimitGroup',
]

export default function RateLimitSetting({ options }: { options: OptionsAPI }) {
  const { get, getBool, getNumber, patch } = options
  const enabled = getBool('ModelRequestRateLimitEnabled')
  const duration = getNumber('ModelRequestRateLimitDurationMinutes', 1)
  const total = getNumber('ModelRequestRateLimitCount', 0)
  const success = getNumber('ModelRequestRateLimitSuccessCount', 1000)
  const groupRows = parseRateLimitGroup(get('ModelRequestRateLimitGroup', '{}'))

  const validate = () => {
    const msg = validateJson(get('ModelRequestRateLimitGroup'))
    if (msg) return `ModelRequestRateLimitGroup: ${msg}`
    const rowMsg = validateRateLimitGroupRows(groupRows)
    if (rowMsg) return rowMsg
    if (success < 1) return '全局成功次数须 ≥ 1'
    if (total < 0) return '全局总请求数须 ≥ 0'
    return null
  }

  return (
    <SettingSectionCard
      title="模型请求速率限制"
      subtitle="按用户 ID 限流；优先 token 分组，无 token 分组时用用户分组"
      keys={KEYS}
      options={options}
      validate={validate}
    >
      <Alert
        type="info"
        className="mb-4"
        content={
          <div className="space-y-1 text-sm">
            <div className="flex flex-wrap gap-2 items-center">
              <span>策略摘要：</span>
              <Pill variant={enabled ? 'success' : 'neutral'}>{enabled ? '已启用' : '已关闭'}</Pill>
              <span>
                周期 {duration} 分钟 · 全局总请求 {total === 0 ? '不限制' : total} · 全局成功 {success}
              </span>
              {groupRows.length > 0 ? (
                <Pill variant="info">{groupRows.length} 个分组覆盖</Pill>
              ) : null}
            </div>
            <div className="text-xs text-gray-500">总请求含失败；成功次数仅计 2xx。分组 JSON 优先级高于全局。</div>
          </div>
        }
      />
      <Form layout="vertical">
        <div className="grid md:grid-cols-2 gap-3">
          <Form.Item label="启用速率限制" extra="即时保存；可能影响高并发性能">
            <InstantSwitch options={options} optionKey="ModelRequestRateLimitEnabled" />
          </Form.Item>
          <Form.Item label="限制周期（分钟）">
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={duration}
              onChange={(v) => patch({ ModelRequestRateLimitDurationMinutes: v ?? 1 })}
            />
          </Form.Item>
          <Form.Item label="每周期最多请求次数" extra="含失败，0 表示不限制">
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={total}
              onChange={(v) => patch({ ModelRequestRateLimitCount: v ?? 0 })}
            />
          </Form.Item>
          <Form.Item label="每周期最多成功次数" extra="仅计成功请求，须 ≥ 1">
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={success}
              onChange={(v) => patch({ ModelRequestRateLimitSuccessCount: v ?? 1000 })}
            />
          </Form.Item>
        </div>
        <Form.Item label="分组速率限制" extra='格式 {"组名": [总请求数, 成功次数]}，覆盖全局阈值'>
          <RateLimitGroupEditor
            value={get('ModelRequestRateLimitGroup', '{}')}
            onChange={(v) => patch({ ModelRequestRateLimitGroup: v })}
          />
        </Form.Item>
        <Collapse bordered={false} className="settings-advanced-collapse">
          <Collapse.Item header="高级：原始 JSON" name="json">
            <JsonField
              value={get('ModelRequestRateLimitGroup', '{}')}
              onChange={(v) => patch({ ModelRequestRateLimitGroup: v })}
              rows={8}
              placeholder={'{\n  "default": [200, 100],\n  "vip": [0, 1000]\n}'}
            />
          </Collapse.Item>
        </Collapse>
      </Form>
    </SettingSectionCard>
  )
}
